import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AsyncCombobox } from "@/components/app/AsyncCombobox";
import { Building2, AlertTriangle } from "lucide-react";

const err = (e) => e?.response?.data?.detail || e?.message || "Request failed";

export function EndClientBar({ orderId }) {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/end-client/orders/${orderId}`).then((r) => setD(r.data)).catch(() => setD(null)), [orderId]);
  useEffect(() => { load(); }, [load]);
  const fetcher = useCallback(async (q) => (await api.get(`/end-client/orders/${orderId}/subsidiaries`, { params: { q } })).data.items.map((s) => ({ value: s.id, label: s.name, sub: s.code })), [orderId]);
  if (!d || !d.facilitator) return null;
  const set = async (id) => {
    setBusy(true);
    try { const { data } = await api.put(`/end-client/orders/${orderId}`, { company_id: id }); toast.success(`End client set to ${data.end_client.name} · ${data.applied.stock_units} stock unit(s) updated`); setEdit(false); load(); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const ec = d.end_client;
  if (ec && !edit) return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs" data-testid="end-client-bar">
      <Building2 className="h-4 w-4 text-emerald-700" />
      <span className="text-slate-600">Subsidiary / End client:</span>
      <b className="text-slate-900" data-testid="end-client-name">{ec.name}</b>
      <span className="text-slate-500">via {d.tenant?.name} · printed on every label</span>
      <button onClick={() => setEdit(true)} className="ml-auto rounded-md border border-slate-300 bg-white px-2 py-1 font-semibold hover:bg-slate-50" data-testid="end-client-change">Change</button>
    </div>
  );
  return (
    <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs" data-testid="end-client-bar">
      <div className="flex items-center gap-2 font-semibold text-amber-900"><AlertTriangle className="h-4 w-4" />Choose subsidiary first. {d.tenant?.name} is a facilitator for {d.subsidiary_count} companies, so labels print the subsidiary's name.</div>
      {!!d.suggestions.length && <div className="flex flex-wrap items-center gap-1.5"><span className="text-slate-600">Suggested:</span>
        {d.suggestions.map((s) => <button key={s.id} disabled={busy} onClick={() => set(s.id)} className="rounded-full border border-amber-400 bg-white px-2.5 py-1 font-semibold text-slate-900 hover:bg-amber-100" title={`Found in ${s.reasons.join(", ")}`} data-testid={`end-client-suggest-${s.id}`}>{s.name} <span className="font-normal text-slate-500">· {s.reasons.join(", ")}</span></button>)}</div>}
      <div className="flex flex-wrap items-center gap-2"><div className="w-72"><AsyncCombobox value="" selectedLabel="" onChange={(id) => id && set(id)} fetcher={fetcher} placeholder="Search subsidiary…" emptyText="No subsidiary found." minChars={1} testId="end-client-picker" /></div>
        {ec && <button onClick={() => setEdit(false)} className="text-slate-600 underline" data-testid="end-client-cancel">Keep {ec.name}</button>}</div>
    </div>
  );
}
